// src/components/categories/CategoryDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchCategory,
  deleteCategory,
  fetchCategoryStats,
  clearCategoryError,
} from '../../store/actions/categoryActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';

const CategoryDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { 
    selectedCategory, 
    loading, 
    error,
    stats 
  } = useSelector((state) => state.categories);

  const [activeTab, setActiveTab] = useState('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const typeLabels = {
    GENERAL: 'Général',
    FAQ: 'FAQ',
    BLOG: 'Blog',
    PRODUCT: 'Produit',
    SUPPORT: 'Support',
  };

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      textDecoration: 'none',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    categoryIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2.5rem',
    },
    categoryInfo: {
      flex: 1,
    },
    categoryName: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    categoryMeta: {
      display: 'flex',
      gap: '2rem',
      flexWrap: 'wrap',
    },
    metaItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metaLabel: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    metaValue: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '2rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    tabContent: {
      padding: '2rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '2rem',
    },
    infoSection: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
      paddingBottom: '0.5rem',
      borderBottom: '2px solid #f3f4f6',
    },
    infoList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    infoItem: {
      display: 'flex',
      padding: '0.75rem 0',
      borderBottom: '1px solid #f3f4f6',
    },
    infoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    infoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    childrenList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    childItem: {
      padding: '0.75rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      border: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    childName: {
      fontWeight: 500,
      color: '#111827',
    },
    childType: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchCategory(id));
      dispatch(fetchCategoryStats(id));

      console.log(selectedCategory);
      
    }
  }, [dispatch, id]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteCategory(id));
      navigate('/admin/categories');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !selectedCategory) {
    return <LoadingSpinner fullScreen />;
  }

  if (!selectedCategory && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Catégorie non trouvée"
        />
        <Link to="/admin/categories" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'info', label: 'Informations' },
    { id: 'children', label: `Sous-catégories (${selectedCategory.children?.length || 0})` },
    { id: 'usage', label: 'Utilisation' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/categories')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails de la catégorie</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/categories/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearCategoryError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div
            style={{
              ...styles.categoryIcon,
              backgroundColor: selectedCategory.color || '#f3f4f6',
            }}
          >
            {selectedCategory.image ? (
              <img
                src={selectedCategory.image.storagePath}
                alt={selectedCategory.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0.5rem' }}
              />
            ) : (
              selectedCategory.icon || '📁'
            )}
          </div>
          <div style={styles.categoryInfo}>
            <h2 style={styles.categoryName}>{selectedCategory.name}</h2>
            <div style={styles.categoryMeta}>
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Type</span>
                <span style={styles.metaValue}>{typeLabels[selectedCategory.type] || selectedCategory.type}</span>
              </div>
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Slug</span>
                <span style={styles.metaValue}>/{selectedCategory.slug}</span>
              </div>
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Statut</span>
                <span
                  style={{
                    ...styles.badge,
                    ...(selectedCategory.isActive ? styles.activeBadge : styles.inactiveBadge),
                  }}
                >
                  {selectedCategory.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
              {selectedCategory.parent && (
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Catégorie parente</span>
                  <span style={styles.metaValue}>
                    <Link to={`/admin/categories/${selectedCategory.parent.id}/details`} style={{ color: '#2563eb' }}>
                      {selectedCategory.parent.name}
                    </Link>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>FAQs</div>
            <div style={styles.statValue}>{stats?.faqCount || 0}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Articles de blog</div>
            <div style={styles.statValue}>{stats?.blogPostCount || 0}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Sous-catégories</div>
            <div style={styles.statValue}>{selectedCategory.children?.length || 0}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Ordre d'affichage</div>
            <div style={styles.statValue}>{selectedCategory.order || 0}</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'info' && (
            <div style={styles.infoGrid}>
              {/* Description */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Description</h3>
                <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '1.6' }}>
                  {selectedCategory.description || 'Aucune description'}
                </p>
              </div>

              {/* SEO Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>SEO</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Meta titre</span>
                    <span style={styles.infoValue}>{selectedCategory.metaTitle || '—'}</span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Meta description</span>
                    <span style={styles.infoValue}>{selectedCategory.metaDescription || '—'}</span>
                  </li>
                </ul>
              </div>

              {/* Metadata */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Métadonnées</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Créé le</span>
                    <span style={styles.infoValue}>{formatDate(selectedCategory.createdAt)}</span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Modifié le</span>
                    <span style={styles.infoValue}>{formatDate(selectedCategory.updatedAt)}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'children' && (
            <div>
              {selectedCategory.children && selectedCategory.children.length > 0 ? (
                <div style={styles.childrenList}>
                  {selectedCategory.children.map((child) => (
                    <div key={child.id} style={styles.childItem}>
                      <div>
                        <div style={styles.childName}>
                          <Link to={`/admin/categories/${child.id}/details`} style={{ color: '#2563eb' }}>
                            {child.name}
                          </Link>
                        </div>
                        <div style={styles.childType}>
                          {typeLabels[child.type] || child.type} • Ordre: {child.order || 0}
                        </div>
                      </div>
                      <span
                        style={{
                          ...styles.badge,
                          ...(child.isActive ? styles.activeBadge : styles.inactiveBadge),
                        }}
                      >
                        {child.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune sous-catégorie
                </div>
              )}
            </div>
          )}

          {activeTab === 'usage' && (
            <div style={styles.infoGrid}>
              {/* FAQs */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>FAQs récentes</h3>
                {stats?.recentFaqs && stats.recentFaqs.length > 0 ? (
                  <ul style={styles.infoList}>
                    {stats.recentFaqs.map((faq) => (
                      <li key={faq.id} style={styles.infoItem}>
                        <span style={styles.infoLabel}>{new Date(faq.createdAt).toLocaleDateString()}</span>
                        <span style={styles.infoValue}>
                          <Link to={`/admin/faqs/${faq.id}/details`} style={{ color: '#2563eb' }}>
                            {faq.question}
                          </Link>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div style={styles.emptyState}>Aucune FAQ récente</div>
                )}
              </div>

              {/* Blog Posts */}
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>Articles récents</h3>
                {stats?.recentBlogPosts && stats.recentBlogPosts.length > 0 ? (
                  <ul style={styles.infoList}>
                    {stats.recentBlogPosts.map((post) => (
                      <li key={post.id} style={styles.infoItem}>
                        <span style={styles.infoLabel}>{new Date(post.createdAt).toLocaleDateString()}</span>
                        <span style={styles.infoValue}>
                          <Link to={`/admin/blog/${post.id}/details`} style={{ color: '#2563eb' }}>
                            {post.title}
                          </Link>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div style={styles.emptyState}>Aucun article récent</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer la catégorie"
        message={`Êtes-vous sûr de vouloir supprimer la catégorie "${selectedCategory?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default CategoryDetails;